import React, { useState, useEffect } from "react";
import axios from "axios";
import { X, Image as ImageIcon, Edit, Trash2, Plus, ArrowLeft, Save } from "lucide-react";
import LoadingIndicator from "../../components/common/loadingIndicator/LoadingIndicator";
import Toast from "../../components/common/toast/Toast";

interface Banner {
    id: number;
    title?: string;
    webImageUrl: string;
    webAltText?: string;
    mobileImageUrl: string;
    mobileAltText?: string;
    isActive: boolean;
    createdAt: string;
}

const Banners: React.FC = () => {
    const [banners, setBanners] = useState<Banner[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState<'list' | 'edit' | 'create'>('list');
    const [selectedBanner, setSelectedBanner] = useState<Banner | null>(null);

    // Toast State
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [toastType, setToastType] = useState<"success" | "error" | "warning">("success");

    // Form State
    const [title, setTitle] = useState("");
    const [webImage, setWebImage] = useState<File | null>(null);
    const [webImagePreview, setWebImagePreview] = useState<string | null>(null);
    const [webAltText, setWebAltText] = useState("");
    const [mobileImage, setMobileImage] = useState<File | null>(null);
    const [mobileImagePreview, setMobileImagePreview] = useState<string | null>(null);
    const [mobileAltText, setMobileAltText] = useState("");
    const [isActive, setIsActive] = useState(true);

    const API_URL = (process.env.REACT_APP_API_BASE_URL as string) || "http://localhost:8080";

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        try {
            setLoading(true);
            const response = await axios.get<Banner[]>(`${API_URL}/banner`);
            setBanners(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching banners:", error);
            setLoading(false);
        }
    };

    const handleWebImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setWebImage(file);
            setWebImagePreview(URL.createObjectURL(file));
        }
    };

    const handleMobileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setMobileImage(file);
            setMobileImagePreview(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setTitle("");
        setWebImage(null);
        setWebImagePreview(null);
        setWebAltText("");
        setMobileImage(null);
        setMobileImagePreview(null);
        setMobileAltText("");
        setIsActive(true);
        setSelectedBanner(null);
    };

    const handleCreateClick = () => {
        resetForm();
        setViewMode('create');
    };

    const handleEditClick = (banner: Banner) => {
        setSelectedBanner(banner);
        setTitle(banner.title || "");
        setWebImagePreview(banner.webImageUrl);
        setWebAltText(banner.webAltText || "");
        setMobileImagePreview(banner.mobileImageUrl);
        setMobileAltText(banner.mobileAltText || "");
        setIsActive(banner.isActive);
        setViewMode('edit');
    };

    const handleDelete = async (id: number) => {
        if (window.confirm("Are you sure you want to delete this banner?")) {
            try {
                await axios.delete(`${API_URL}/banner/${id}`);
                fetchBanners();
                setToastMessage("Banner deleted successfully!");
                setToastType("success");
                setShowToast(true);
            } catch (error) {
                console.error("Error deleting banner:", error);
                setToastMessage("Failed to delete banner.");
                setToastType("error");
                setShowToast(true);
            }
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (viewMode === 'create' && (!webImage || !mobileImage)) {
            setToastMessage("Please select both web and mobile images.");
            setToastType("warning");
            setShowToast(true);
            return;
        }

        setIsSubmitting(true);
        const formData = new FormData();
        formData.append("title", title);
        formData.append("webAltText", webAltText);
        formData.append("mobileAltText", mobileAltText);
        formData.append("isActive", String(isActive));

        if (webImage) formData.append("webImage", webImage);
        if (mobileImage) formData.append("mobileImage", mobileImage);

        try {
            if (viewMode === 'create') {
                await axios.post(`${API_URL}/banner`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setToastMessage("Banner created successfully!");
            } else if (viewMode === 'edit' && selectedBanner) {
                await axios.patch(`${API_URL}/banner/${selectedBanner.id}`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                setToastMessage("Banner updated successfully!");
            }
            setToastType("success");
            setShowToast(true);
            fetchBanners();
            setViewMode('list');
            resetForm();
        } catch (error) {
            console.error("Error saving banner:", error);
            setToastMessage("Failed to save banner.");
            setToastType("error");
            setShowToast(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (loading && viewMode === 'list' && banners.length === 0) {
        return <div className="p-4">Loading banners...</div>;
    }

    return (
        <div className="p-6 relative">
            {isSubmitting && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
                        <LoadingIndicator />
                        <p className="mt-4 text-gray-700 font-medium">Processing...</p>
                    </div>
                </div>
            )}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">
                    {viewMode === 'list' ? 'Banner Management' : viewMode === 'create' ? 'Add New Banner' : 'Edit Banner'}
                </h1>
                {viewMode === 'list' && (
                    <button
                        onClick={handleCreateClick}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700"
                    >
                        <Plus size={20} /> Add Banner
                    </button>
                )}
                {viewMode !== 'list' && (
                    <button
                        onClick={() => { setViewMode('list'); resetForm(); }}
                        className="bg-gray-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-600"
                    >
                        <ArrowLeft size={20} /> Back to List
                    </button>
                )}
            </div>

            {viewMode === 'list' ? (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {banners.map((banner) => (
                                <tr key={banner.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <img src={banner.webImageUrl} alt="Preview" className="h-12 w-24 object-cover rounded" />
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {banner.title || `Banner #${banner.id}`}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${banner.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {banner.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => handleEditClick(banner)} className="text-indigo-600 hover:text-indigo-900 mr-4">
                                            <Edit size={18} />
                                        </button>
                                        <button onClick={() => handleDelete(banner.id)} className="text-red-600 hover:text-red-900">
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {banners.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-4 text-center text-gray-500">
                                        No banners found. Create one to get started.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow p-6 max-w-4xl mx-auto">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Banner Title</label>
                                <input
                                    type="text"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    className="w-full border border-gray-300 rounded-md p-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="e.g., Summer Sale 2025"
                                />
                            </div>

                            {/* Web Image Section */}
                            <div className="border p-4 rounded-lg bg-gray-50">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <ImageIcon size={20} /> Web Image (Desktop)
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-gray-700 mb-1">
                                            Upload Image <span className="text-gray-400 text-xs">(Recommended: 1920x1080px)</span>
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleWebImageChange}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                    </div>
                                    {webImagePreview && (
                                        <div className="mt-2">
                                            <p className="text-xs text-gray-500 mb-1">Preview:</p>
                                            <img src={webImagePreview} alt="Web Preview" className="w-full h-40 object-cover rounded border" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm text-gray-700 mb-1">Alt Text</label>
                                        <input
                                            type="text"
                                            value={webAltText}
                                            onChange={(e) => setWebAltText(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2"
                                            placeholder="Describe the image for accessibility"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Image Section */}
                            <div className="border p-4 rounded-lg bg-gray-50">
                                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
                                    <ImageIcon size={20} /> Mobile Image
                                </h3>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm text-gray-700 mb-1">
                                            Upload Image <span className="text-gray-400 text-xs">(Recommended: 1080x1920px)</span>
                                        </label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleMobileImageChange}
                                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                        />
                                    </div>
                                    {mobileImagePreview && (
                                        <div className="mt-2">
                                            <p className="text-xs text-gray-500 mb-1">Preview:</p>
                                            <img src={mobileImagePreview} alt="Mobile Preview" className="w-32 h-48 object-cover rounded border mx-auto" />
                                        </div>
                                    )}
                                    <div>
                                        <label className="block text-sm text-gray-700 mb-1">Alt Text</label>
                                        <input
                                            type="text"
                                            value={mobileAltText}
                                            onChange={(e) => setMobileAltText(e.target.value)}
                                            className="w-full border border-gray-300 rounded-md p-2"
                                            placeholder="Describe the image for accessibility"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-span-2">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        id="isActive"
                                        checked={isActive}
                                        onChange={(e) => setIsActive(e.target.checked)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                    <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                                        Active (Visible on Homepage)
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 pt-4 border-t">
                            <button
                                type="button"
                                onClick={() => { setViewMode('list'); resetForm(); }}
                                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 flex items-center gap-2"
                            >
                                <Save size={18} />
                                {viewMode === 'create' ? 'Create Banner' : 'Update Banner'}
                            </button>
                        </div>
                    </form>
                </div>
            )}
            {showToast && (
                <Toast
                    type={toastType}
                    message={toastMessage}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div>
    );
};

export default Banners;
